/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
// import { setCurrentUser } from "../store/actions/authActions.js";
import { usePostHog } from '@posthog/react';
import { NavLink } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";
import TableSearchBar from "../../components/TableSearchBar";
import { FormateDate } from "../../utils/FormatDate";
import instance, { fetchAllDepartments } from "../../api/api";
import { Loader } from "../../components/Loader";
import PerspectiveBadge from "../../components/PerspectiveBadge";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
} from "@tanstack/react-table";

type Risk = {
  id: string;
  riskNumber: string;
  riskName: string;
  perspective: string;
  objective: string;
  riskManagementPlan: string;
  actionOwner: string;
  cycleLength: string;
  reassessmentDate: string;
  // NEW
  isClosed?: boolean;
  status?: string; // "Closed" | "Open"
};

function AdminRiskReassessment() {
  const posthog = usePostHog();
  const [moreDetailsRequested, setMoreDetailsRequested] = useState(false);

  const [currentRiskDetails, setCurrentRiskDetails] = useState<any>([]);
  const [isLoadingRisks, setIsLoadingRisks] = useState(false);
  const [allRisks, setAllRisks] = useState<any[]>([]);

  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>([
    { id: "riskNumber", desc: false },
  ]);

  // const [isApprovingRisks, setIsApprovingRisks] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const editDrawerRef = useRef<any>(null);

  const [departments, setDepartments] = useState<any[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [highlightedRiskId, setHighlightedRiskId] = useState<any>(null);

  const closeDrawerRef = useRef<any>(null);

  useEffect(() => {
    const fetchDepartments = async () => {
      setDepartments(await fetchAllDepartments());
    };
    fetchDepartments();
    posthog.capture('page_viewed', { page: 'admin_risk_reassessment' });
  }, []);

  const getAllRisks = async () => {
    try {
      setIsLoadingRisks(true);
      let riskRegisterResponse;
      let closedRisks;
      if (selectedDepartment === "" || selectedDepartment === "All") {
        riskRegisterResponse = await instance.get(
          "/api/admin/riskRegister/riskRegisters/selections/COMPLETED"
        );

        // closed risks
        closedRisks = await instance.get(
          "/api/admin/riskRegister/riskRegisters/selections/CLOSED"
        );
      } else {
        riskRegisterResponse = await instance.get(
          `/api/admin/riskRegister/riskRegisters/department?departmentName=${selectedDepartment}`
        );
        // closed risks
        closedRisks = await instance.get(
          "/api/admin/riskRegister/riskRegisters/selections/CLOSED"
        );
      }
      const riskReassessment = await instance.get(
        "/api/admin/riskReassessments/all"
      );

      const riskManagement = await instance.get(
        "/api/admin/riskManagement/riskManagements/all"
      );

      setIsLoadingRisks(false);

      const combinedRisks = (() => {
        const registerList = riskRegisterResponse.data.data as any[];
        const reassessmentList = riskReassessment.data.data as any[];

        return (
          reassessmentList
            // keep only reassessments whose riskRegister exists in the (optionally filtered) register list
            .filter((reass) =>
              registerList.some((reg) => reg.id === reass.riskRegisterId)
            )
            .map((reass) => {
              const register = registerList.find(
                (reg) => reg.id === reass.riskRegisterId
              );

              return {
                ...register,
                reassessmentId: reass.id,
                reassessmentDate: reass.reassessmentDate
                  ? FormateDate(reass.reassessmentDate)
                  : "----/--/--",
                cycleLength: reass.circleLenght ?? 0,
                riskRegisterId: reass.riskRegisterId,
              };
            })
        );
      })();

      console.log("combinedRisks", combinedRisks);

      const combinedRisksWithManagement = combinedRisks.map((risk) => {
        const managements = riskManagement.data.data.find(
          (management: any) => management.riskRegisterId === risk.id
        );
        
        // Check if completion date is in the past
        const completionDate = managements?.completionDate 
          ? new Date(managements.completionDate) 
          : null;
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to midnight for accurate date comparison
        
        const isPastCompletionDate = completionDate && completionDate < today;
        
        return {
          ...risk,
          riskManagementPlan: managements ? managements.riskManagementPlan : "",
          actionOwner: managements ? managements.actionOwner : "",
          completionDate: managements
            ? FormateDate(managements.completionDate)
            : "----/--/--",
          isClosed: isPastCompletionDate || false,
          status: isPastCompletionDate ? "CLOSED" : "OPEN",
        };
      });

      const closedRisksList = closedRisks.data.data as any[];

      const closedRisksAugmented = closedRisksList.map((closed) => {
        const managements = riskManagement.data.data.find(
          (m: any) => m.riskRegisterId === closed.id
        );
        console.log("this is the closed risk managements: ", managements);
        return {
          ...closed,
          reassessmentId: null,
          reassessmentDate: "----/--/--",
          cycleLength: 0,
          riskRegisterId: closed.id,
          riskManagementPlan: managements ? managements.riskManagementPlan : "",
          actionOwner: managements ? managements.actionOwner : "",
          isClosed: true,
          status: managements.status === "ACTIVE_MANAGEMENT" ? "OPEN" : "CLOSED",
        };
      });

      // Merge open (reassessment) risks and closed risks, avoid duplicates
      const filteredClosedRisksAugmented = closedRisksAugmented.filter(
        (risk) =>
          risk.riskManagementPlan &&
          risk.riskManagementPlan.trim() !== "" &&
          risk.riskManagementPlan.trim() !== "--"
      );

      const finalRisks = [
        ...combinedRisksWithManagement,
        ...filteredClosedRisksAugmented.filter(
          (c) => !combinedRisksWithManagement.some((r) => r.id === c.id)
        ),
      ];

      setAllRisks(finalRisks);
    } catch (error) {
      setIsLoadingRisks(false);
      console.error(error);
    }
  };
  useEffect(() => {
    getAllRisks();
  }, [selectedDepartment]);

  const handleEditRiskDrawer = (riskId: string) => {
    const risk = allRisks.find((risk: Risk) => risk.id === riskId);
    setIsEditDrawerOpen(true);
    setCurrentRiskDetails(risk);
    setHighlightedRiskId(riskId);
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        editDrawerRef.current &&
        !editDrawerRef.current.contains(event.target)
      ) {
        setIsEditDrawerOpen(false);
        setHighlightedRiskId(null);
      }
    };

    if (isEditDrawerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      setHighlightedRiskId(null);
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditDrawerOpen]);

  const handlePageChange = (pageNumber: any) => {
    setCurrentPage(pageNumber);
  };

  // Calculate the items to display based on the current page and items per page
  const paginatedRisks = React.useMemo(() => {
    let risksToDisplay = allRisks;

    // Apply search filter
    if (searchTerm) {
      risksToDisplay = risksToDisplay.filter((risk: any) =>
        Object.values(risk).some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return risksToDisplay.slice(indexOfFirstItem, indexOfLastItem);
  }, [allRisks, currentPage, itemsPerPage, searchTerm]);

  const totalPages = Math.ceil(allRisks.length / itemsPerPage);

  const handleAddReassessment = async (e: any) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await instance.post(
        `/api/admin/riskReassessments/riskReassessment/create/${currentRiskDetails.id}`,
        {
          reassessmentDate: currentRiskDetails.reassessmentDate,
        }
      );
      if (closeDrawerRef.current) {
        closeDrawerRef.current.click();
      }
      setIsSubmitting(false);
      posthog.capture('admin_risk_reassessment_added', { riskId: currentRiskDetails.id });
      await getAllRisks();
    } catch (error) {
      setIsSubmitting(false);
      return error;
    }
  };
  const handleMoreDetailsRequest = async (e: any) => {
    e.preventDefault();
    setMoreDetailsRequested(!moreDetailsRequested);
  };

  const columnHelper = createColumnHelper<Risk>();
  const columns = [
    columnHelper.accessor("riskNumber", {
      header: () => <span className="whitespace-nowrap">Risk Number</span>,
      cell: (info) => (
        <span className="whitespace-nowrap font-bold">
          {info.renderValue()}
        </span>
      ),
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("riskName", {
      header: () => <span className="whitespace-nowrap">Risk Name</span>,
      cell: (info) => info.getValue(),
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor((row) => row.perspective, {
      id: "perspective",
      cell: (info) => <PerspectiveBadge value={info.getValue()} />,
      header: () => <span>Perspective</span>,
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("objective", {
      header: () => "Objective",
      cell: (info) => info.renderValue(),
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("riskManagementPlan", {
      header: () => "Management Plan",
      cell: (info) => info.renderValue(),
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("actionOwner", {
      header: () => "Action Owner",
      cell: (info) => info.renderValue(),
      footer: (info) => info.column.id,
    }),
    // NEW Status column
    columnHelper.accessor("status", {
      header: () => "Status",
      cell: (info) => {
        const value = info.getValue();
        const isClosed = info.row.original.isClosed;
        return (
          <span
            className={`font-semibold ${
              isClosed ? "text-red-600" : "text-green-600"
            }`}
          >
            {value}
          </span>
        );
      },
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("reassessmentDate", {
      header: () => "Reassessment Date",
      cell: (info) => info.renderValue(),
      footer: (info) => info.column.id,
    }),
    columnHelper.display({
      id: "actions",
      header: () => <span>Actions</span>,
      cell: ({ row }) => {
        const disabled = row.original.isClosed;
        return (
          <button
            type="button"
            disabled={disabled}
            className={`w-auto flex justify-center items-center border text-xs px-4 py-2 rounded-lg me-2 mb-2 font-medium transition-colors
              ${
                disabled
                  ? "bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed"
                  : "text-caribbeanCurrent border-caribbeanCurrent hover:text-black hover:bg-white hover:border-2 hover:border-caribbeanCurrent hover:bg-caribbeanCurrent dark:border-blue-500 dark:text-blue-500 dark:hover:text-white dark:hover:bg-caribbeanCurrent"
              }
            `}
            data-drawer-target="drawer-edit-plan"
            data-drawer-show="drawer-edit-plan"
            data-drawer-placement="right"
            aria-controls="drawer-edit-plan"
            onClick={() => {
              if (!disabled) handleEditRiskDrawer(row.original.id);
            }}
            title={disabled ? "Closed risk cannot be edited" : "Edit"}
          >
            {!disabled && (
              <svg
                className="w-4 h-4 text-white-800 dark:text-white me-2"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"
                />
              </svg>
            )}
            Edit
          </button>
        );
      },
    }),
  ];

  const table = useReactTable<Risk>({
    data: paginatedRisks,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  return (
    <>
      {/* <!-- Breadcrumb --> */}
      <nav
        className="justify-between  py-3 text-gray-700  sm:flex bg-gray-50"
        aria-label="Breadcrumb"
      >
        <ol className="inline-flex items-center mb-3 space-x-1 md:space-x-2 rtl:space-x-reverse sm:mb-0">
          <li>
            <div className="flex items-center">
              <NavLink
                to=""
                className="text-xl font-semibold text-gray-700 hover:text-blue-600  dark:text-gray-400 dark:hover:text-white"
              >
                Risk Reassessment
              </NavLink>
            </div>
          </li>
        </ol>
      </nav>

      {/* <p className="text-caribbeanCurrent font-semibold">
          Due date: 14 September, 2024
        </p> */}
      <form className="flex max-w-sm my-4 justify-center items-center">
        <label
          htmlFor="countries"
          className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
        >
          Department
        </label>
        <select
          id="countries"
          className="ml-5 bg-gray-50 border border-caribbeanCurrent text-gray-900 text-md rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          onChange={(e) => setSelectedDepartment(e.target.value)}
        >
          <option>All</option>
          {departments &&
            departments.map((department, index) => (
              <option key={index}>{department.name}</option>
            ))}
        </select>
      </form>

      <div className="border-2 border-dashed rounded-lg border-gray-300 dark:border-gray-600 mb-4">
        <div className="p-5 text-lg font-semibold text-left rtl:text-right text-gray-900 bg-white dark:text-white dark:bg-gray-800 sticky top-0 z-20">
          <div className="w-full flex items-center justify-between">
            <p>Department Risks</p>
            <div className="flex flex-row gap-4 items-center justify-center">
              <div className="">
                <TableSearchBar
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  placeholder="Search risks..."
                />
              </div>
            </div>
          </div>
        </div>
        <div className="rt-wrap relative sm:rounded-lg max-h-[calc(100vh-300px)] overflow-y-auto">
          <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
            <thead className="text-sm text-jet font-bold bg-gray-50 dark:bg-gray-700 dark:text-gray-400 sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isSorted = header.column.getIsSorted();
                    return (
                      <th
                        scope="col"
                        key={header.id}
                        className="px-6 py-3"
                        aria-sort={
                          isSorted === "asc"
                            ? "ascending"
                            : isSorted === "desc"
                            ? "descending"
                            : "none"
                        }
                      >
                        {header.isPlaceholder ? null : (
                          <button
                            type="button"
                            className={`flex items-center gap-1 select-none ${
                              header.column.getCanSort()
                                ? "cursor-pointer"
                                : "cursor-default"
                            }`}
                            onClick={header.column.getToggleSortingHandler()}
                            title={
                              header.column.getCanSort() ? "Sort" : undefined
                            }
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getCanSort() && (
                              <span className="text-xs">
                                {isSorted === "asc"
                                  ? "▲"
                                  : isSorted === "desc"
                                  ? "▼"
                                  : "⇅"}
                              </span>
                            )}
                          </button>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            {isLoadingRisks ? (
              <tbody>
                <tr>
                  <td colSpan={7} className="text-center">
                    <div className="flex justify-center items-center h-96">
                      <Loader size={"xl"} />
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="text-jet font-medium text-xs">
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`bg-gray border-b border-t hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 ${
                      highlightedRiskId === row.original.id ? "bg-gray-200" : ""
                    }`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td className="px-6 py-4 align-top" key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
          {/* Pagination */}
          <div className="mt-4 pl-10 pr-10 pb-5">
            <nav aria-label="Page navigation">
              <ul className="flex justify-between">
                <li className="flex flex-row items-center">
                  <div>
                    <p>Show rows per page</p>
                  </div>
                  <div>
                    <select
                      className="ml-2 bg-gray-50 border border-caribbeanCurrent text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    >
                      <option>25</option>
                      <option>50</option>
                      <option>100</option>
                    </select>
                  </div>
                </li>
                {/* {Array.from({ length: totalPages }, (_, index) => (
              <li key={index}>
                <button
                  className={`px-3 py-2 leading-tight ${
                    currentPage === index + 1
                      ? "text-blue-600 bg-blue-50 border border-blue-300"
                      : "text-gray-500 bg-white border border-gray-300"
                  } hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white`}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </button>
              </li>
            ))} */}
                <li className="flex flex-row gap-1 items-center text-gray-500">
                  <div>
                    <p>
                      {currentPage} of {totalPages === 0 ? 1 : totalPages}
                    </p>
                  </div>
                  <div>
                    <button
                      className="px-3 py-2 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg rounded-r-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      {"<"}
                    </button>
                  </div>
                  <div>
                    <button
                      className="px-3 py-2 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg rounded-l-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      {">"}
                    </button>
                  </div>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* drawers */}

      {/* Risk Monitoring Edit Drawer */}
      {allRisks.length > 0 && (
        <div
          id="drawer-edit-plan"
          ref={editDrawerRef}
          className={`fixed top-0 right-0 z-50 h-screen p-4 overflow-y-auto transition-transform ${
            isEditDrawerOpen ? "translate-x-0" : "translate-x-full"
          } bg-white w-full md:w-1/4 dark:bg-gray-800`}
          tabIndex={-1}
          aria-labelledby="drawer-edit-plan-item-label"
        >
          <h5
            id="drawer-label"
            className="inline-flex items-center mb-6 text-base font-semibold text-gray-500  dark:text-gray-400"
          >
            Risk Monitoring Edit
          </h5>
          <button
            type="button"
            data-drawer-hide="drawer-edit-plan"
            aria-controls="drawer-edit-plan"
            ref={closeDrawerRef}
            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 absolute top-2.5 end-2.5 inline-flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white"
            onClick={() => setIsEditDrawerOpen(false)}
          >
            <svg
              className="w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
            <span className="sr-only">Close menu</span>
          </button>
          <form key={currentRiskDetails?.id} onSubmit={handleAddReassessment}>
            <div className="border-2 border-dashed rounded-lg border-gray-300 dark:border-gray-600 mb-4 p-4">
              <div className="mb-6">
                <label
                  htmlFor="riskGoals"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Perspective
                </label>
                <input
                  type="text"
                  id="perspective"
                  value={currentRiskDetails.perspective}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder="Perspective goes here"
                  required
                  disabled
                  readOnly
                />
              </div>
              <div className="mb-6">
                <label
                  htmlFor="riskGoals"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Objective
                </label>
                <input
                  type="text"
                  id="objective"
                  value={currentRiskDetails.objective}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder="Objective goes here"
                  required
                  disabled
                  readOnly
                />
              </div>
              <div className="mb-6">
                <label
                  htmlFor="riskName"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Risk Name
                </label>
                <input
                  type="text"
                  id="riskName"
                  value={currentRiskDetails.riskName}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder="Risk title goes here"
                  required
                  disabled
                  readOnly
                />
              </div>
              {moreDetailsRequested && (
                <>
                  <div className="mb-6">
                    <label
                      htmlFor="source"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Source
                    </label>
                    <input
                      type="text"
                      id="source"
                      value={
                        currentRiskDetails.source
                          ? currentRiskDetails.source
                          : ""
                      }
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      placeholder="Source goes here"
                      required
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="mb-6">
                    <label
                      htmlFor="description"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Description
                    </label>
                    <textarea
                      readOnly
                      id="description"
                      rows={4}
                      disabled
                      className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      placeholder=""
                      value={currentRiskDetails.description}
                    ></textarea>
                  </div>
                  <div className="mb-6">
                    <label
                      htmlFor="rootCauses"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Root Causes
                    </label>
                    <textarea
                      id="rootCauses"
                      readOnly
                      disabled
                      rows={4}
                      className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      placeholder=""
                      value={currentRiskDetails.rootCause}
                    ></textarea>
                  </div>
                </>
              )}
              <div className="mb-6 flex justify-center">
                <button
                  onClick={handleMoreDetailsRequest}
                  className="text-center font-bold text-caribbeanCurrent flex items-center"
                >
                  {moreDetailsRequested ? (
                    <>
                      Collapse{" "}
                      <span>
                        <IoIosArrowDown />
                      </span>
                    </>
                  ) : (
                    <>
                      See More Details{" "}
                      <span>
                        <IoIosArrowDown />
                      </span>
                    </>
                  )}
                </button>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="managementPlan"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Risk Monitoring Plan
                </label>
                <textarea
                  id="managementPlan"
                  rows={2}
                  className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder="Management plan goes here..."
                  readOnly
                  disabled
                  defaultValue={currentRiskDetails.riskManagementPlan}
                ></textarea>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="actionOwner"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Action Owner
                </label>
                <input
                  type="text"
                  id="actionOwner"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder="Action owner goes here"
                  readOnly
                  disabled
                  defaultValue={currentRiskDetails.actionOwner}
                />
              </div>

              {/* <div className="mb-6">
              <label
                htmlFor="actionOwner"
                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >
                Cycle Length
              </label>
              <select
                id="actionOwner"
                className=" bg-gray-50 border border-caribbeanCurrent  text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              >
                <option>270 Days - mid</option>
              </select>
            </div> */}

              <div className="">
                <label
                  htmlFor="completionDate"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Reassessment Date
                </label>
                <div className="flex border border-3 border-black rounded-lg items-center">
                  <div className="border p-2 rounded-tl-lg rounded-bl-lg bg-caribbeanCurrent text-white px-5 whitespace-nowrap">
                    Select Date
                  </div>
                  <div className="">
                    <input
                      defaultValue={currentRiskDetails.reassessmentDate}
                      onChange={(e) => {
                        setCurrentRiskDetails((prevState: any) => ({
                          ...prevState,
                          reassessmentDate: e.target.value,
                        }));
                      }}
                      className="w-full appearance-none flex justify-between border-0 border-none"
                      type="date"
                    ></input>
                    {/* <input type="date" className="appearance-none w-full pl-4 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" /> */}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-4">
                <button
                  disabled={isSubmitting}
                  type="submit"
                  className={`justify-center items-center inline-flex text-white bg-caribbeanCurrent hover:bg-white hover:text-caribbeanCurrent hover:border-2 hover:border-caribbeanCurrent w-full focus:ring-4 focus:ring-caribbeanCurrent font-medium rounded-lg text-sm px-5 py-2.5 mb-2 dark:bg-caribbeanCurrent dark:hover:bg-caribbeanCurrent focus:outline-none dark:focus:ring-caribbeanCurrent block ${
                    isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isSubmitting ? <Loader size="sm" /> : "Save Risk"}
                </button>

                <button
                  type="button"
                  className="justify-center items-center inline-flex text-caribbeanCurrent border-2 border-caribbeanCurrent hover:bg-caribbeanCurrent hover:text-white w-full focus:ring-4 focus:ring-caribbeanCurrent font-medium rounded-lg text-sm px-5 py-2.5 mb-2 dark:bg-caribbeanCurrent dark:hover:bg-caribbeanCurrent focus:outline-none dark:focus:ring-caribbeanCurrent block"
                  data-drawer-hide="drawer-assessment-risk-score-item"
                  aria-controls="drawer-assessment-risk-score-item"
                  onClick={() => setIsEditDrawerOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default AdminRiskReassessment;
